import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search, MapPin, Loader2, ImagePlus, X, Star, Check } from "lucide-react";
import { api, apiError, peso } from "@/lib/api";
import { ConsumerHeader } from "@/pages/ConsumerHome";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

import { AMENITIES, PROPERTY_TYPE_GROUPS, propertyTypeLabel, SALE_TYPES, saleTypeFormLabel } from "@/lib/taxonomy";
const FURNISHING = ["Bare", "Semi-Furnished", "Fully Furnished"];

const NONE = "__none__";

function LocationPicker({ code, name, onPick }) {
  const [q, setQ] = useState(""); const [res, setRes] = useState([]); const [open, setOpen] = useState(false);
  const box = useRef(null); const t = useRef(null);
  useEffect(() => {
    if (!q.trim()) { setRes([]); return; }
    clearTimeout(t.current);
    t.current = setTimeout(async () => {
      try { const { data } = await api.get("/geo/search", { params: { q: q.trim(), limit: 12 } }); setRes(data.results || []); setOpen(true); } catch { setRes([]); }
    }, 280);
    return () => clearTimeout(t.current);
  }, [q]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={box} className="relative" data-testid="sell-location-picker">
      {name ? (
        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-border bg-white px-3 h-11">
          <MapPin className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-medium text-slate-700 truncate">{name}</span>
          <button type="button" data-testid="sell-location-change" onClick={() => onPick({ code: "", name: "" })} className="ml-auto text-xs text-slate-500 hover:text-slate-800">Change</button>
        </div>
      ) : (
        <div className="relative mt-1.5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="sell-location-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a city or municipality…" className="w-full h-11 pl-9 pr-3 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
        </div>
      )}
      {open && !name && res.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-border bg-white shadow-lg">
          {res.map((r) => (
            <li key={r.code}><button type="button" data-testid={`sloc-option-${r.code}`} onClick={() => { onPick({ code: r.code, name: r.name }); setQ(""); setRes([]); setOpen(false); }} className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50">
              <span className="truncate">{r.name}{r.parentName ? <span className="text-slate-400 font-normal">, {r.parentName}</span> : null}</span>
              <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{r.level === "CITY_MUNICIPALITY" ? "City / Municipality" : r.level === "PROVINCE" ? "Province" : "Region"}</span>
            </button></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function SellSubmit() {
  const nav = useNavigate();
  const fileRef = useRef();
  const [step, setStep] = useState(1); // 1 = form, 2 = review
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [f, setF] = useState({
    title: "", property_type: "Condominium", sale_type: "", city: "", location_code: "", neighborhood: "", project: "",
    bedrooms: "1", bathrooms: "1", parking_slots: "", floor_area_sqm: "", lot_area_sqm: "",
    price: "", furnishing: "Bare", completion_status: "", amenities: [], description: "",
    submitter_relationship: "OWNER", attestation: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (v) => setF((p) => ({ ...p, amenities: p.amenities.includes(v) ? p.amenities.filter((x) => x !== v) : [...p.amenities, v] }));

  const onFiles = async (files) => {
    const arr = Array.from(files).slice(0, 20 - photos.length);
    setUploading(true);
    for (const file of arr) {
      const fd = new FormData(); fd.append("file", file);
      try { const { data } = await api.post("/upload/temp-photo", fd); setPhotos((p) => [...p, data]); }
      catch (e) { toast.error(apiError(e)); }
    }
    setUploading(false);
  };

  const toReview = () => {
    if (!f.title) return toast.error("Add a property title.");
    if (!f.city) return toast.error("Pick a location (city / municipality).");
    if (!f.price) return toast.error("Add an asking price.");
    if (!f.sale_type) return toast.error("Choose a Sale Type — Developer, Resale, Pasalo or Foreclosed.");
    setStep(2);
  };

  const submit = async () => {
    if (!f.attestation) return toast.error("Please confirm the ownership/authorization attestation.");
    setSaving(true);
    try {
      await api.post("/me/property-submissions", {
        title: f.title, property_type: f.property_type, sale_type: f.sale_type || null, city: f.city, location_code: f.location_code,
        neighborhood: f.neighborhood, project: f.project,
        bedrooms: parseInt(f.bedrooms || 0), bathrooms: parseInt(f.bathrooms || 0),
        parking_slots: f.parking_slots === "" ? null : parseInt(f.parking_slots),
        floor_area_sqm: parseInt(f.floor_area_sqm || 0), lot_area_sqm: parseInt(f.lot_area_sqm || 0),
        price: parseInt(f.price || 0), furnishing: f.furnishing, completion_status: f.completion_status,
        amenities: f.amenities, features: [], description: f.description,
        photo_paths: photos.map((p) => p.path),
        submitter_relationship: f.submitter_relationship, attestation: f.attestation,
      });
      toast.success("Property submitted for review");
      nav("/");
    } catch (e) { toast.error(apiError(e)); setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="sell-submit-page">
      <ConsumerHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => (step > 1 ? setStep(1) : nav("/sell"))} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>

        {step === 1 && (
          <div className="space-y-6" data-testid="sell-form-step">
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900">List your property</h1>
              <p className="text-slate-500 mt-1 text-sm">Submit the details below. Our team reviews every submission before it goes live.</p>
            </div>

            {/* Photos */}
            <div>
              <Label>Photos <span className="text-slate-400 font-normal">(JPG/PNG/WEBP, up to 20 · first is the cover)</span></Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {photos.map((p, idx) => (
                  <div key={p.path} className="relative h-24 w-32 rounded-lg overflow-hidden border border-border group">
                    <img src={p.url} alt="" className="h-full w-full object-cover" />
                    {idx === 0 && <span className="absolute bottom-1 left-1 text-[10px] font-semibold bg-accent text-accent-foreground px-1.5 py-0.5 rounded flex items-center gap-0.5"><Star className="h-2.5 w-2.5" /> Cover</span>}
                    <button type="button" data-testid={`sell-remove-photo-${idx}`} onClick={() => setPhotos((ps) => ps.filter((x) => x.path !== p.path))} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-[#0A1B33]/60 text-white flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button type="button" data-testid="sell-upload-photos" onClick={() => fileRef.current?.click()} disabled={uploading || photos.length >= 20} className="h-24 w-32 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-slate-400 hover:border-accent hover:text-accent transition-colors">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ImagePlus className="h-5 w-5" /><span className="text-[11px] mt-1">Add photos</span></>}
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files.length && onFiles(e.target.files)} />
              </div>
            </div>

            {/* Property Details */}
            <div className="space-y-4">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Property Details</h2>
              <div><Label>Property title</Label><Input data-testid="sell-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. 2BR Corner Unit at Uptown Parksuites" className="mt-1.5" /></div>
              <div>
                <Label>Property type</Label>
                <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}><SelectTrigger data-testid="sell-property-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROPERTY_TYPE_GROUPS.map((g) => (<SelectGroup key={g.label}><SelectLabel>{g.label}</SelectLabel>{g.items.map((t) => <SelectItem key={t} value={t}>{propertyTypeLabel(t)}</SelectItem>)}</SelectGroup>))}</SelectContent></Select>
              </div>
              <div>
                <Label>Sale type</Label>
                <Select value={f.sale_type || undefined} onValueChange={(v) => set("sale_type", v)}>
                  <SelectTrigger data-testid="sell-sale-type" className="mt-1.5"><SelectValue placeholder="Select how it's being sold" /></SelectTrigger>
                  <SelectContent>{SALE_TYPES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`sell-sale-type-option-${s.value}`}>{s.formLabel}</SelectItem>)}</SelectContent>
                </Select>
                <p className="mt-1.5 text-xs text-slate-500">How this property is being offered.</p>
              </div>
              <div><Label>Location <span className="text-slate-400 font-normal">(city / municipality)</span></Label>
                <LocationPicker code={f.location_code} name={f.city} onPick={({ code, name }) => setF((p) => ({ ...p, location_code: code, city: name }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Neighborhood</Label><Input data-testid="sell-neighborhood" value={f.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} placeholder="e.g. BGC" className="mt-1.5" /></div>
                <div><Label>Project / development</Label><Input data-testid="sell-project" value={f.project} onChange={(e) => set("project", e.target.value)} placeholder="Optional" className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Bedrooms</Label>
                  <Select value={f.bedrooms} onValueChange={(v) => set("bedrooms", v)}><SelectTrigger data-testid="sell-bedrooms" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{["0", "1", "2", "3", "4", "5"].map((t) => <SelectItem key={t} value={t}>{t === "0" ? "Studio" : t}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Bathrooms</Label>
                  <Select value={f.bathrooms} onValueChange={(v) => set("bathrooms", v)}><SelectTrigger data-testid="sell-bathrooms" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{["0", "1", "2", "3", "4", "5"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Parking</Label><Input data-testid="sell-parking" type="number" min="0" value={f.parking_slots} onChange={(e) => set("parking_slots", e.target.value)} placeholder="—" className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Floor area (sqm)</Label><Input data-testid="sell-floor-area" type="number" min="0" value={f.floor_area_sqm} onChange={(e) => set("floor_area_sqm", e.target.value)} placeholder="78" className="mt-1.5" /></div>
                <div><Label>Lot area (sqm)</Label><Input data-testid="sell-lot-area" type="number" min="0" value={f.lot_area_sqm} onChange={(e) => set("lot_area_sqm", e.target.value)} placeholder="Optional" className="mt-1.5" /></div>
              </div>
            </div>

            {/* Selling Details */}
            <div className="space-y-4">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Selling Details</h2>
              <div><Label>Asking price (₱)</Label><CurrencyInput data-testid="sell-price" value={f.price} onValueChange={(v) => set("price", v)} placeholder="8,500,000" className="mt-1.5" /></div>
              <div><Label>Furnishing</Label>
                <Select value={f.furnishing} onValueChange={(v) => set("furnishing", v)}><SelectTrigger data-testid="sell-furnishing" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{FURNISHING.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div>
                <Label>Amenities &amp; features</Label>
                <div className="flex flex-wrap gap-2 mt-2">{AMENITIES.map((c) => (
                  <button key={c} type="button" data-testid={`sell-amenity-${c.replace(/[^a-zA-Z]/g, "")}`} onClick={() => toggle(c)} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${f.amenities.includes(c) ? "bg-accent text-accent-foreground border-accent" : "bg-white border-border text-slate-600 hover:border-slate-400"}`}>{c}</button>
                ))}</div>
              </div>
              <div><Label>Description</Label><Textarea data-testid="sell-description" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Highlights, view, turnover date…" className="mt-1.5" /></div>
            </div>

            {/* Relationship */}
            <div className="space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Your Relationship to the Property</h2>
              <Select value={f.submitter_relationship} onValueChange={(v) => set("submitter_relationship", v)}>
                <SelectTrigger data-testid="sell-relationship" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OWNER">I am the property owner</SelectItem>
                  <SelectItem value="AUTHORIZED_REPRESENTATIVE">I am authorized to submit this property</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl p-3">
                <Checkbox data-testid="sell-attestation" checked={f.attestation} onCheckedChange={(v) => set("attestation", !!v)} className="mt-0.5" />
                <span>I confirm that the information I provided is accurate and that I am the property owner or am authorized to submit this property.</span>
              </label>
            </div>

            <Button data-testid="sell-to-review" onClick={toReview} className="w-full h-11 bg-accent text-accent-foreground rounded-xl font-semibold">Review submission <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="sell-review-step">
            <h1 className="font-display text-2xl font-bold text-slate-900">Review your submission</h1>
            <div className="bg-card border border-border rounded-xl divide-y divide-slate-100">
              {[
                ["Title", f.title],
                ["Type", f.property_type],
                ["Sale type", saleTypeFormLabel(f.sale_type) || "—"],
                ["Location", f.city || "—"],
                ["Asking price", f.price ? peso(Number(f.price)) : "—"],
                ["Bedrooms", f.bedrooms], ["Bathrooms", f.bathrooms],
                ["Parking", f.parking_slots || "—"],
                ["Floor area", f.floor_area_sqm ? `${f.floor_area_sqm} sqm` : "—"],
                ["Furnishing", f.furnishing],
                ["Amenities", f.amenities.join(", ") || "None"],
                ["Photos", String(photos.length)],
                ["Relationship", f.submitter_relationship === "OWNER" ? "Owner" : "Authorized representative"],
              ].map(([k, v], i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800 text-right">{v}</span></div>
              ))}
            </div>
            {!f.attestation && <p className="text-xs text-red-600" data-testid="sell-attestation-warning">Please go back and confirm the attestation before submitting.</p>}
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-11 rounded-xl">Edit</Button>
              <Button data-testid="sell-submit" onClick={submit} disabled={saving} className="flex-1 h-11 bg-accent text-accent-foreground rounded-xl font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit Property <Check className="h-4 w-4 ml-1" /></>}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
